// Canned scenarios for the customer portal's artwork preflight check journey
// (/artwork-check, /artwork-check/:checkId), used by the mock
// CustomerDataAdapter (customer-portal-mock-adapter.ts) and by the upload
// page's "Try an example" strip (mock mode only — see
// PortalArtworkCheckPage.tsx's header for why real mode hides it).
//
// The real backend (artwork_preflight_module, mounted as
// customer_artwork_check_router) genuinely inspects the uploaded PDF, so
// there is no way to reproduce these scenarios against it — a filename has
// no bearing on the real verdict. This file exists purely so a reviewer can
// see every verdict shape (and the failure state) without needing
// specifically-crafted PDFs on hand, in mock mode only.
//
// Because the mock adapter has no real PDF inspection either, a genuinely
// selected file always resolves to the "clean" scenario — see
// `scenarioForFileName` below. The other scenarios are reachable through the
// upload page's labelled "Try an example" buttons, which construct a `File`
// under one of the magic names below.

import type { EngineIssue } from "./customer-portal-artwork-wording";

export type ArtworkCheckScenarioId =
  "clean" | "blocking" | "fixable-only" | "mixed" | "unmapped" | "will-fail";

interface ArtworkCheckScenario {
  id: ArtworkCheckScenarioId;
  /** Shown on the upload page's "Try an example" button. */
  label: string;
  issues: EngineIssue[];
  /** True for the one scenario that simulates the check itself failing
   *  (the real backend's RaisingPreflightClient/tool-failure path) rather
   *  than producing a verdict. */
  fails?: boolean;
}

export const ARTWORK_CHECK_SCENARIOS: readonly ArtworkCheckScenario[] = [
  {
    id: "clean",
    label: "A clean file",
    issues: [],
  },
  {
    id: "blocking",
    label: "One blocking issue",
    issues: [
      { check_name: "Font not embedded", severity: "blocking", hit_count: 6 },
    ],
  },
  {
    id: "fixable-only",
    label: "A few things we'll handle",
    issues: [
      {
        check_name: "Bleed missing for page",
        severity: "fixable",
        hit_count: 1,
      },
      { check_name: "Spot color is used", severity: "fixable", hit_count: 14 },
      {
        check_name:
          "Resolution of color and grayscale images larger than 16x16 pixel is less than 200 ppi inside BleedBox",
        severity: "fixable",
        hit_count: 3,
      },
    ],
  },
  {
    id: "mixed",
    label: "A blocking issue and some we'll fix",
    issues: [
      {
        check_name: "Effective CMYK ink coverage above 245%",
        severity: "blocking",
        hit_count: 4,
      },
      {
        check_name: "PDF/X Output Intent not CMYK",
        severity: "fixable",
        hit_count: 1,
      },
      {
        check_name: "Black text <  pt is not set to overprint",
        severity: "fixable",
        hit_count: 9,
      },
      {
        check_name:
          "Resolution of color and grayscale images larger than 16x16 pixel is less than 200 ppi inside BleedBox",
        severity: "fixable",
        hit_count: 2,
      },
    ],
  },
  // A check the backend has no wording for holds the file: the customer is
  // told only the neutral status, and neither check name below is ever shown
  // (doc 60 §5.8; Ben, 5 Oct). A held file must never read as "clean" or as a
  // list of findings, so PortalArtworkCheckResultPage.stories.tsx asserts both.
  {
    id: "unmapped",
    label: "A check we don't have wording for yet",
    issues: [
      {
        check_name: "Overprint preview mismatch on spot plate",
        severity: "blocking",
        hit_count: 1,
      },
      {
        check_name: "Transparency flattening required",
        severity: "fixable",
        hit_count: 3,
      },
    ],
  },
  {
    id: "will-fail",
    label: "Simulate a failed check",
    issues: [],
    fails: true,
  },
];

const SCENARIO_FILE_NAME = (id: ArtworkCheckScenarioId): string =>
  `sample-${id}.pdf`;

/** Which scenario a submitted file resolves to. Sample-button uploads carry
 *  one of the magic names above; any other file name (a real customer
 *  upload) resolves to "clean" — there is no real PDF inspection to do
 *  anything else. */
export function scenarioForFileName(fileName: string): ArtworkCheckScenario {
  const byName = ARTWORK_CHECK_SCENARIOS.find(
    (s) => SCENARIO_FILE_NAME(s.id) === fileName,
  );
  return byName ?? ARTWORK_CHECK_SCENARIOS[0];
}

export function sampleFileNameFor(id: ArtworkCheckScenarioId): string {
  return SCENARIO_FILE_NAME(id);
}
